import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GlassesRegular } from '@fluentui/react-icons';
import { Box, Button, Card } from '@mui/material';
import { ScoreChip } from '@ui/components/common/chips';
import { SDataGrid } from '@ui/components/common/grid/SDataGrid';
import IncidentPreviewDrawer from '../IncidentPreviewDrawer';
import useIncidentsDataGridStyles from './useIncidentsDataGridStyles';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { fetchPreviewIncident, setAddFilter, setPageNumber, setPageSize } from '@application/redux/Incidents/incident-slice';
import { convertUtcToLocal } from '@application/utilities/date-time-formater';
import { useParams } from 'react-router-dom';
const IncidentsDataGrid = ({ fullHeight = true, rows, loading, hiddenColumns = [], showFilters, handleEnableFilters }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { incidents } = useSelector((state) => state.incidentDetail);
    const [isLoading, setisLoading] = useState(false);
    const [rowStyle, setRowStyle] = useState('highlight-inline');
    const { incidentsGridCardStyles } = useIncidentsDataGridStyles();
    const [showIncidentPreviewDrawer, setShowIncidentPreviewDrawer] = useState(false);
    const handleOpenIncidentPreviewDrawer = async (rowId) => {
        setisLoading(true);
        await dispatch(fetchPreviewIncident(rowId)).unwrap();
        setRowStyle('');
        setisLoading(false);
        setShowIncidentPreviewDrawer(true);
    };
    const handleCloseIncidentPreviewDrawer = () => {
        setShowIncidentPreviewDrawer(false);
    };
    const { id } = useParams();
    const handleFilters = (filters) => {
        let incidentDate_from = filters.incidentDate_from;
        let incidentDate_to = filters.incidentDate_to;
        if (incidentDate_from) {
            incidentDate_from = dayjs(incidentDate_from).startOf('day').toDate();
        }
        if (incidentDate_to) {
            incidentDate_to = dayjs(incidentDate_to).endOf('day').toDate();
        }
        if (incidentDate_from &&
            incidentDate_to &&
            dayjs(incidentDate_to).isBefore(dayjs(incidentDate_from), 'day')) {
            toast.error('End date cannot be before start date');
            return;
        }
        const filtersArray = Object.entries({ ...filters, incidentDate_from, incidentDate_to })
            .filter(([_, value]) => (typeof value === 'object' ? value !== null : value?.trim() !== ''))
            .map(([key, value]) => ({
            key,
            value: typeof value === 'object' && value?.toISOString ? value.toISOString() : value
        }));
        if (filtersArray.length > 0) {
            dispatch(setAddFilter(filtersArray));
        }
        else {
            dispatch(setAddFilter([]));
        }
    };
    const columns = [
        {
            field: 'systemName',
            headerName: t('SystemName'),
            minWidth: 150,
            flex: 1,
            editable: false,
            sortable: false,
            type: 'string'
        },
        {
            field: 'assetName',
            headerName: t('AssetName'),
            minWidth: 150,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'licensePlate',
            headerName: t('License#'),
            minWidth: 150,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'location',
            headerName: t('Location'),
            minWidth: 150,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'incidentDate',
            headerName: t('Date/Time'),
            minWidth: 400,
            flex: 1.4,
            editable: false,
            type: 'dateTime',
            valueFormatter: (params) => convertUtcToLocal(params)
        },
        {
            field: 'score',
            headerName: t('Score'),
            type: 'number',
            align: 'left',
            headerAlign: 'left',
            minWidth: 140,
            flex: 1,
            editable: false,
            renderCell: (params) => _jsx(ScoreChip, { type: 'warning', label: params.value })
        },
        {
            field: '',
            headerName: '',
            minWidth: 120,
            display: 'flex',
            align: 'center',
            editable: false,
            sortable: false,
            type: 'actions',
            renderCell: (params) => {
                return (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, pr: '6px' }, children: _jsx(Button, { variant: 'outlined', color: 'info', startIcon: _jsx(GlassesRegular, { fontSize: 20 }), onClick: () => handleOpenIncidentPreviewDrawer(params.row.id), children: t('Preview') }) }));
            }
        }
    ];
    return (_jsxs(Fragment, { children: [_jsx(Card, { className: 'incidents-grid-card', sx: incidentsGridCardStyles, children: _jsx(Box, { className: 'table-container', height: fullHeight ? 'calc(100dvh - 240px)' : 'auto', children: _jsx(SDataGrid, { rows: rows, columns: columns, serverSidePagination: true, showFilters: showFilters, handleEnableFilters: handleEnableFilters, loading: isLoading || loading, onFilterChange: handleFilters, totalRows: incidents?.data.totalRecords, pageSizeOptions: [10, 20, 50, 100], hideColumns: hiddenColumns, initialState: {
                            pagination: {
                                paginationModel: {
                                    pageSize: incidents?.data.pageSize || 10,
                                    page: incidents?.data.pageNumber || 0
                                }
                            }
                        }, onPaginationModelChange: (model) => {
                            dispatch(setPageNumber(model.page + 1));
                            dispatch(setPageSize(model.pageSize));
                        }, getRowClassName: (params) => {
                            if (id) {
                                return params.id.toString() === id ? rowStyle : '';
                            }
                        }, filters: {}, setFilters: () => { } }) }) }), showIncidentPreviewDrawer && (_jsx(IncidentPreviewDrawer, { open: showIncidentPreviewDrawer, onClose: handleCloseIncidentPreviewDrawer }))] }));
};
export default IncidentsDataGrid;
